import { signal } from "signlets";

import type { PlaneMode } from "../gen/schema";
import { type CommandLong, planeModeMap } from "../gen/schema";
import { calculateBestGlideSpeed } from "./calculations";
import { delay } from "./common";
import { elevation } from "./elevation";
import type { Mavlink } from "./mavlink";
import { dropMission } from "./mission";
import type { Drop, State } from "./model";
import { positionEmpty } from "./position";
import { createVehicleMissions } from "./vehicle-missions";

const initialState: State = {
  time: 0,
  bootTime: 0,
  simulation: false,
  flying: false,
  armed: false,
  position: [0, 0, 0],
  orientation: [0, 0, 0],
  target: [0, 0, 0],
  heading: 0,
  batteryVoltage: 0,
  airspeed: 0,
  path: [],
};

export type Vehicle = Awaited<ReturnType<typeof createVehicle>>;

export const createVehicle = async (mavlink: Mavlink) => {
  const [state, setState] = signal(initialState);

  const vehicleMissions = createVehicleMissions(mavlink);

  const update = (_: Partial<State>) => setState({ ...state(), ..._ });

  const reset = () => update({ path: [] });

  const unsubscribe = mavlink.receive(({ type, message }) => {
    const time = Date.now();
    switch (type) {
      case "GLOBAL_POSITION_INT": {
        const lat = message.lat / 1e7;
        const lon = message.lon / 1e7;
        const altitude = message.alt / 1e3;
        const heading = message.hdg / 100;
        update({ time, position: [lon, lat, altitude], heading });
        break;
      }
      case "ATTITUDE": {
        const { yaw, pitch, roll } = message;
        update({ time, orientation: [yaw, pitch, roll] });
        break;
      }
      case "POSITION_TARGET_GLOBAL_INT": {
        const lat = message.latInt / 1e7;
        const lon = message.lonInt / 1e7;
        const altitude = message.alt;
        update({ time, target: [lon, lat, altitude] });
        break;
      }
      case "HEARTBEAT": {
        const armed = message.baseMode.includes("SAFETY_ARMED");
        const flying = ["ACTIVE", "CRITICAL"].includes(message.systemStatus);
        update({ time, armed, flying });
        break;
      }
      case "STATUSTEXT": {
        console.log(message.text);
        if (message.text === "ArduPilot Ready") reset();
        break;
      }
      case "SYS_STATUS": {
        const batteryVoltage = message.voltageBattery / 1000;
        update({ time, batteryVoltage });
        break;
      }
      case "VFR_HUD": {
        const { airspeed } = message;
        update({ time, airspeed });
        break;
      }
    }
    if ("timeBootMs" in message && typeof message.timeBootMs === "number")
      update({ bootTime: message.timeBootMs });
  });

  const heartbeat = () =>
    mavlink.send("HEARTBEAT", {
      type: "GCS",
      autopilot: "INVALID",
      baseMode: [],
      customMode: 0,
      systemStatus: "ACTIVE",
      mavlinkVersion: 3,
    });

  const sendCommandLong = async (
    message: Partial<CommandLong>,
    cancel?: Promise<never>,
  ) => {
    const result = await mavlink.retry(
      "COMMAND_LONG",
      {
        targetSystem: 0,
        targetComponent: 0,
        command: "NAV_DELAY",
        confirmation: 0,
        param1: 0,
        param2: 0,
        param3: 0,
        param4: 0,
        param5: 0,
        param6: 0,
        param7: 0,
        ...message,
      },
      mavlink.receiveOne("COMMAND_ACK", _ => _.command === message.command),
      cancel,
    );
    if (result.result !== "ACCEPTED") console.warn("Command failure", result);
  };

  const getParameter = async (name: string, cancel?: Promise<never>) => {
    const { paramValue: value } = await mavlink.retry(
      "PARAM_REQUEST_READ",
      {
        targetSystem: 0,
        targetComponent: 1,
        paramId: name,
        paramIndex: -1,
      },
      mavlink.receiveOne("PARAM_VALUE", _ => _.paramId === name),
      cancel,
    );
    return Number(value.toPrecision(7));
  };

  const setParameter = async (
    name: string,
    value: number,
    cancel?: Promise<never>,
  ) => {
    await mavlink.retry(
      "PARAM_SET",
      {
        targetSystem: 0,
        targetComponent: 0,
        paramId: name,
        paramValue: value,
        paramType: "REAL32",
      },
      mavlink.receiveOne("PARAM_VALUE", _ => _.paramId === name),
      cancel,
    );
  };

  const armDisarm = (arm: boolean) =>
    sendCommandLong({
      command: "COMPONENT_ARM_DISARM",
      param1: arm ? 1 : 0,
      param2: 21196,
    });

  const arm = () => armDisarm(true);

  const setMode = (mode: PlaneMode) =>
    sendCommandLong({
      command: "DO_SET_MODE",
      param1: 1,
      param2: planeModeMap.get(mode),
    });

  const writeDrop = async (drop: Drop) => {
    const {
      glideSlope,
      dropAltitude,
      dropSpeed,
      dropDirection,
      windSpeed,
      windDirection,
    } = drop;

    const speed = calculateBestGlideSpeed(drop);

    await Promise.all([
      setParameter("AIRSPEED_CRUISE", speed),
      setParameter("SCR_USER2", glideSlope),
      setParameter("SA_AUTO_SELECT", 1),
    ]);
    await vehicleMissions.write(dropMission(drop));

    if (state().simulation) {
      const [lon, lat] = drop.drop;
      const ground = await elevation([lon, lat]);
      const altitude = dropAltitude - ground;
      await Promise.all([
        setParameter("SIM_GD2K_VEL", dropSpeed),
        setParameter("SIM_GD2K_DIR", dropDirection),
        setParameter("SIM_GD2K_ALT", altitude),
        setParameter("SIM_GD2K_YLOSS", 1),
        setParameter("SIM_WIND_SPD", windSpeed),
        setParameter("SIM_WIND_DIR", windDirection),
      ]);
    }
  };

  const surfacesTest = async () => {
    await setMode("MANUAL");
    for (let t = 0; t < 4.0; t += 0.02) {
      const pitch = Math.cos(t);
      const roll = Math.sin(t);
      mavlink.send("MANUAL_CONTROL", {
        target: 0,
        x: pitch * 1000,
        y: roll * 1000,
        z: 0,
        r: 0,
        buttons: 0,
        buttons2: 0,
        enabledExtensions: 0,
        s: 0,
        t: 0,
        aux1: 0,
        aux2: 0,
        aux3: 0,
        aux4: 0,
        aux5: 0,
        aux6: 0,
      });
      await delay(20);
    }
  };

  const calibrateMagnetometer = async (heading: number) => {
    await sendCommandLong({
      command: "FIXED_MAG_CAL_YAW",
      param1: heading,
    });
    await delay(1000);
  };

  const step = () => {
    heartbeat();
    if (positionEmpty(state().position)) return;
    const path = [state().position, ...state().path].slice(0, 1000);
    update({ path });
  };

  const interval = setInterval(step, 200);

  const close = () => {
    clearInterval(interval);
    unsubscribe();
    mavlink.close();
  };

  const connect = async () => {
    await mavlink.receiveOne("HEARTBEAT");
    const simSpeedup = getParameter(
      "SIM_SPEEDUP",
      delay(500).then(() => {
        throw "canceled";
      }),
    );
    try {
      const simulation = !!(await simSpeedup);
      update({ simulation });
    } catch {
      simSpeedup.catch(() => {});
    }
  };

  await connect();

  return {
    state,
    writeDrop,
    arm,
    calibrateMagnetometer,
    surfacesTest,
    close,
  };
};
